import { rememberRatio } from '@/imageRatio';
import { APP_PLATFORM, APP_VERSION } from '@/appVersion';
import { readStored, removeStored, writeStored } from '@/storage';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

const API = `${BASE}/api/v1`;

export const toRelativeMedia = (url: string | null): string | null =>
  url ? url.replace(/^https?:\/\/[^/]+/, BASE) : url;

export interface Pictured {
  image_url: string | null;
  image_width?: number | null;
  image_height?: number | null;
}

export function withPicture<T extends Pictured>(item: T): T {
  const image_url = toRelativeMedia(item.image_url);
  if (image_url && item.image_width && item.image_height) {
    rememberRatio(image_url, item.image_width, item.image_height);
  }
  return { ...item, image_url };
}

export const thumbUrl = (url: string): string => url.replace(/\/([^/]+)$/, '/thumbs/$1');

let token: string | null = null;

export function setToken(value: string): void {
  token = value;
  writeStored('tg_jwt', value);
}

export function loadToken(): string | null {
  return (token ??= readStored('tg_jwt'));
}

export const isGuest = (): boolean => !loadToken();

export const EMPTY_LIST = { data: [], count: 0 };

export function clearToken(): void {
  token = null;
  removeStored('tg_jwt');
}

export type Lang = 'uzl' | 'uzk' | 'ru';

export interface List<T> {
  data: T[];
  count: number;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

const RELAUNCHED_AT = 'auth_relaunched_at';

const RELAUNCH_MIN_MS = 30_000;

const REQUEST_TIMEOUT_MS = 20_000;

function deadline(): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
    : undefined;
}

const RETRY_AFTER_MS = 800;

function worthRetrying(e: unknown): boolean {
  if (e instanceof ApiError) return e.status >= 500;
  return e instanceof TypeError && navigator.onLine !== false;
}

export function request<T>(path: string, init?: RequestInit): Promise<T> {
  const once = () => requestOnce<T>(path, init);
  if ((init?.method ?? 'GET') !== 'GET') return once();
  return once().catch((e: unknown) => {
    if (!worthRetrying(e)) throw e;
    return new Promise<void>((resolve) => setTimeout(resolve, RETRY_AFTER_MS)).then(once);
  });
}

async function requestOnce<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('X-App-Version', APP_VERSION);
  headers.set('X-App-Platform', APP_PLATFORM);
  const t = loadToken();
  if (t) headers.set('Authorization', `Bearer ${t}`);

  const res = await fetch(`${API}${path}`, { signal: deadline(), ...init, headers });
  if (res.status === 401 && !path.startsWith('/login') && t) {
    if (loadToken() !== t) return requestOnce<T>(path, init);
    clearToken();
    const last = Number(sessionStorage.getItem(RELAUNCHED_AT) ?? 0);
    if (Date.now() - last > RELAUNCH_MIN_MS) {
      sessionStorage.setItem(RELAUNCHED_AT, String(Date.now()));
      window.location.reload();
    }
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      message = body?.error?.message ?? body?.detail ?? message;
      // eslint-disable-next-line no-empty
    } catch {}
    throw new ApiError(res.status, message);
  }
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

export function deleteMe(): Promise<void> {
  return request('/users/me', { method: 'DELETE' });
}

export function loginHandoff(handoffToken: string): Promise<{ access_token: string }> {
  return request('/login/handoff', {
    method: 'POST',
    body: JSON.stringify({ handoff_token: handoffToken }),
  });
}

export function retryable(e: unknown): boolean {
  return !(e instanceof ApiError) || e.status >= 500 || e.status === 401;
}
